import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faUser, faGamepad } from '@fortawesome/free-solid-svg-icons';

import { PrimaryButton, SecondaryButton } from '../../../../components/button/Button.jsx';
import useRoleNames from '../../../../hooks/useRoleNames.js';

import '../user-access-registration/AccessManagementInvitationAccept.css';

/**
 * Review card for a self-serve game-access request, opened from the "Review request" button in the
 * game owner's email. Mirrors AccessManagementApproval: the owner sees who is asking and for what,
 * then approves or rejects right here — no trip through the dashboard.
 */
const AccessManagementAccessRequest = ({ request, onApprove, onReject, approveLoading, rejectLoading }) => {
    const { roleLabel } = useRoleNames();

    // The registry stores the requester's email as their userId; userName is the display name and
    // may be absent for an account that never set one.
    const requesterName = request?.userName || request?.userId || '';
    const requesterId = request?.userId || '';
    // gameName / clientName are resolved server-side for the approval queue; the raw code is the
    // fallback so this never renders blank.
    const gameLabel = [request?.gameName, request?.clientName].filter(Boolean).join(' / ')
        || request?.gameClientId
        || '';
    const regionIds = request?.regionIds || [];
    const submitted = request?.lastModifiedDate
        ? new Date(request.lastModifiedDate).toLocaleString()
        : '';

    return (
        <div className="inv-accept-card">
            {/* ===== Header ===== */}
            <div className="inv-accept-header">
                <div className="inv-accept-header-icon-wrapper">
                    <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div className="inv-accept-header-title-section">
                    <h2 className="inv-accept-header-title">Review Access Request</h2>
                    <p className="inv-accept-header-subtitle">
                        Someone has requested access to a game you own. Approve or reject the request below.
                    </p>
                </div>
            </div>

            {/* ===== Scrollable Body ===== */}
            <div className="inv-accept-body">
                {/* Section: Requester Details */}
                <div className="inv-accept-section">
                    <h3 className="inv-accept-section-title">
                        <FontAwesomeIcon icon={faUser} />
                        Requester Details
                    </h3>
                    <div className="inv-accept-fields-grid">
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Name</span>
                            <span className="inv-accept-field-value">{requesterName || 'N/A'}</span>
                        </div>
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">User ID</span>
                            <span className="inv-accept-field-value">{requesterId || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                <hr />

                {/* Section: Access Requested */}
                <div className="inv-accept-section">
                    <h3 className="inv-accept-section-title">
                        <FontAwesomeIcon icon={faGamepad} />
                        Access Requested
                    </h3>
                    <div className="inv-accept-fields-grid">
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Game / Build</span>
                            <span className="inv-accept-field-value">{gameLabel || 'N/A'}</span>
                        </div>
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Role Requested</span>
                            <span className="inv-accept-field-value">
                                {request?.role ? roleLabel(request.role) : 'N/A'}
                            </span>
                        </div>
                        {submitted && (
                            <div className="inv-accept-field">
                                <span className="inv-accept-field-label">Submitted</span>
                                <span className="inv-accept-field-value">{submitted}</span>
                            </div>
                        )}
                    </div>

                    {regionIds.length > 0 && (
                        <div className="inv-accept-field">
                            <span className="inv-accept-field-label">Regions</span>
                            <div className="inv-accept-tags-section">
                                {regionIds.map((region) => (
                                    <span key={region} className="inv-accept-tag">{region}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ===== Footer ===== */}
            <div className="inv-accept-footer">
                <SecondaryButton onClick={onReject} disabled={rejectLoading || approveLoading}>
                    {rejectLoading ? 'Rejecting...' : 'Reject'}
                </SecondaryButton>
                <PrimaryButton onClick={onApprove} disabled={approveLoading || rejectLoading}>
                    {approveLoading ? 'Approving...' : 'Approve'}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default AccessManagementAccessRequest;
